import { createReducer, on } from '@ngrx/store';
import { TournamentsWithPagination } from '../../../tournament/models/tournaments.interface';
import { Game, Genre, Platform } from '../../models/games.interface';
import { GamesActions } from '../actions/actions-type.action';
import { Region } from 'src/app/all-tournaments/models/api/responses/regions.response';

export interface GamesState {
    genres: Genre[];
    games: Game[];
    regions: Region[];
    regionsV2: Region[];
    platforms: Platform[];
    upcoming: TournamentsWithPagination;
    ongoing: TournamentsWithPagination;
    past: TournamentsWithPagination;
    gamesLoaded: boolean;
    genresLoaded: boolean;
    platformsLoaded: boolean;
    inGameIdUnique: boolean | null;
    isCheckingGameId: boolean;
    isLoading: boolean;
}

const initialState: GamesState = {
    genres: [],
    games: [],
    regions: [],
    regionsV2: [],
    platforms: [],
    upcoming: {
        items: [],
        page: 0,
        totalPages: 0,
        isExhausted: false,
        isLoading: false,
    },
    ongoing: {
        items: [],
        page: 0,
        totalPages: 0,
        isExhausted: false,
        isLoading: false,
    },
    past: {
        items: [],
        page: 0,
        totalPages: 0,
        isExhausted: false,
        isLoading: false,
    },
    gamesLoaded: false,
    genresLoaded: false,
    platformsLoaded: false,
    isLoading: false,
    inGameIdUnique: null,
    isCheckingGameId: false,
};

export const gamesReducer = createReducer(
    initialState,
    on(GamesActions.checkUniqueAccountInGameId, (state) => ({
        ...state,
        inGameIdUnique: null,
        isCheckingGameId: true,
    })),
    on(GamesActions.checkUniqueAccountInGameIdSuccess, (state, action) => ({
        ...state,
        inGameIdUnique: action.payload,
        isCheckingGameId: false,
    })),
    on(GamesActions.checkUniqueAccountInGameIdFailure, (state, action) => ({
        ...state,
        inGameIdUnique: action.payload,
        isCheckingGameId: false,
    })),
    on(GamesActions.fetchGenres, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(GamesActions.fetchGenresSuccess, (state, action) => ({
        ...state,
        genres: action.payload,
        genresLoaded: true,
        isLoading: false,
    })),
    on(GamesActions.fetchGenresFailure, (state) => ({
        ...state,
        isLoading: false,
    })),
    on(GamesActions.fetchGames, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(GamesActions.fetchGamesSuccess, (state, action) => {
        return {
            ...state,
            games: action.payload,
            gamesLoaded: true,
            isLoading: false,
        }
    }),
    on(GamesActions.fetchPlatforms, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(GamesActions.fetchPlatformsSuccess, (state, action) => ({
        ...state,
        platforms: action.payload,
        platformsLoaded: true,
        isLoading: false,
    })),
    on(GamesActions.fetchGamesFailure, (state) => ({
        ...state,
        isLoading: false,
    })),
    on(GamesActions.getGameById, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(GamesActions.getGameByIdSuccess, (state, action) => {
        const incoming = action.payload.data;
        const existing = state.games.find((g) => g.id === incoming.id);
        const merged = existing
            ? {
                  ...existing,
                  ...incoming,
                  gameAccountOptions:
                      (incoming as any).gameAccountOptions && (incoming as any).gameAccountOptions.length
                          ? (incoming as any).gameAccountOptions
                          : (existing as any).gameAccountOptions,
                  platforms:
                      (incoming as any).platforms && (incoming as any).platforms.length
                          ? (incoming as any).platforms
                          : (existing as any).platforms,
              }
            : incoming;
        return {
            ...state,
            games: [
                ...state.games.filter((g) => g.id !== incoming.id),
                merged,
            ],
            isLoading: false,
        };
    }),
    on(GamesActions.getGameByIdFailure, (state) => ({
        ...state,
        isLoading: false,
    })),
    on(GamesActions.fetchTournamentsByGameSuccess, (state, action) => {
        const {
            type,
            response: { items, page, pageSize, total },
        } = action.payload;
        const totalPages = Math.ceil(total / pageSize);
        const isExhausted = page === totalPages;
        return {
            ...state,
            [type]: {
                items: action.payload.patchExistingTournaments
                    ? items
                    : [...state[type].items, ...items],
                page,
                totalPages,
                isExhausted,
                isLoading: false,
            },
            isLoading: false,
        }
    }),
    on(GamesActions.fetchTournamentsByGameFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(GamesActions.fetchTournamentsByGameV2Success, (state, action) => {
        const {
            type,
            response: { items, page, pageSize, total },
        } = action.payload;
        const totalPages = Math.ceil(total / pageSize);
        const isExhausted = page === totalPages;
        return {
            ...state,
            [type]: {
                items: action.payload.patchExistingTournaments
                    ? items
                    : [...state[type].items, ...items],
                page,
                totalPages,
                isExhausted,
                isLoading: false,
            },
            isLoading: false,
        }
    }),
    on(GamesActions.fetchTournamentsByGameV2Failure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(GamesActions.fetchRegionsSuccess, (state, action) => ({
        ...state,
        regions: action.payload
    })),
    on(GamesActions.fetchRegionsV2Success, (state, action) => ({
        ...state,
        regionsV2: action.payload
    })),
    on(GamesActions.fetchFormats, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(GamesActions.fetchFormatsSuccess, (state, action) => ({
        ...state,
        isLoading: false,
        games: state.games.map((g) =>
            g.id === action.payload.gameId
                ? { ...g, formats: action.payload.formats }
                : g
            ),
    })),
    on(GamesActions.fetchFormatsFailure, (state) => ({
        ...state,
        isLoading: false,
    })),
);
